import { useEffect, useMemo, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import type { SeedPageSchema } from "@/lib/kb-api";
import { AutosizeTextarea } from "@/components/kb/AutosizeTextarea";
import { useHermes } from "@/components/hermes/HermesProvider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KB_COPY } from "@/lib/kb-copy";
import { seedFieldStatus, type SeedFieldStatus } from "@/lib/kb-page-status";
import { cn } from "@/lib/utils";

type SeedFormProps = {
  seedDef: SeedPageSchema | undefined;
  structured: Record<string, unknown>;
  body: string;
  disabled?: boolean;
  pageId?: number;
  /**
   * 字段级 AI 异常说明（key → 原因）。有值时标题旁显示橙色异常图标。
   * 数据源预留：可由 page.frontmatter.field_flags / field_anomalies 解析后传入。
   */
  fieldAnomalies?: Record<string, string>;
  onStructuredChange: (structured: Record<string, unknown>) => void;
  onBodyChange: (body: string) => void;
};

const GROUP_ORDER = ["身份", "业务", "联系"] as const;

export function SeedForm({
  seedDef,
  structured,
  body,
  disabled,
  pageId,
  fieldAnomalies,
  onStructuredChange,
  onBodyChange,
}: SeedFormProps) {
  const properties = seedDef?.properties ?? {};
  const anomalies = fieldAnomalies ?? {};
  const { knowledgeEntry, updateKnowledgeEntryStructured } = useHermes();

  useEffect(() => {
    if (pageId && knowledgeEntry?.pageId === pageId) {
      updateKnowledgeEntryStructured(structured);
    }
  }, [structured, pageId, knowledgeEntry?.pageId, updateKnowledgeEntryStructured]);

  const groupedFields = useMemo(() => {
    const entries = Object.entries(properties);
    const hasGroups = entries.some(([, schema]) => schema.ui?.group);

    if (!hasGroups) {
      return [{ title: null as string | null, fields: entries }];
    }

    const buckets = new Map<string | null, Array<[string, (typeof entries)[number][1]]>>();
    for (const entry of entries) {
      const group = entry[1].ui?.group ?? null;
      const bucket = buckets.get(group) ?? [];
      bucket.push(entry);
      buckets.set(group, bucket);
    }

    const ordered: Array<{ title: string | null; fields: typeof entries }> = [];
    for (const title of GROUP_ORDER) {
      const fields = buckets.get(title);
      if (fields?.length) {
        ordered.push({ title, fields });
        buckets.delete(title);
      }
    }
    for (const [title, fields] of buckets) {
      ordered.push({ title, fields });
    }
    return ordered;
  }, [properties]);

  const orphanEntries = useMemo(() => {
    const schemaKeys = new Set(Object.keys(properties));
    return Object.entries(structured).filter(([key, value]) => {
      if (schemaKeys.has(key)) return false;
      if (value == null) return false;
      if (typeof value === "string" && value.trim() === "") return false;
      return true;
    });
  }, [properties, structured]);

  return (
    <div className="space-y-6">
      {groupedFields.map((group) => (
        <div key={group.title ?? "default"} className="space-y-4">
          {group.title ? <h3 className="text-sm font-medium text-foreground">{group.title}</h3> : null}
          {group.fields.map(([key, schema]) => (
            <SeedField
              key={key}
              fieldKey={key}
              schema={schema}
              value={structured[key]}
              anomaly={anomalies[key]}
              disabled={disabled}
              onChange={(value) => onStructuredChange({ ...structured, [key]: value })}
            />
          ))}
        </div>
      ))}

      {orphanEntries.length > 0 ? (
        <div className="space-y-4 rounded-md border border-dashed border-amber-500/40 bg-amber-500/5 px-3 py-3">
          <h3 className="text-sm font-medium text-foreground">其它已写入字段</h3>
          <p className="text-xs text-muted-foreground">
            以下键名不在当前 seed schema 中，仍保存在页面上；可手动改到标准字段后删除。
          </p>
          {orphanEntries.map(([key, value]) => (
            <SeedField
              key={key}
              fieldKey={key}
              schema={{
                type: "string",
                description: key,
                ui: { widget: "textarea" },
              }}
              value={value}
              anomaly={anomalies[key]}
              disabled={disabled}
              onChange={(next) => onStructuredChange({ ...structured, [key]: next })}
            />
          ))}
        </div>
      ) : null}

      <details className="group rounded-md border border-border/60 px-3 py-2">
        <summary className="cursor-pointer text-sm font-medium">
          {KB_COPY.bodyLabel}
          <span className="ml-2 text-xs font-normal text-muted-foreground">（可选）</span>
        </summary>
        <p className="mt-1 text-xs text-muted-foreground">{KB_COPY.bodyHint}</p>
        <AutosizeTextarea
          id="seed-body"
          value={body}
          disabled={disabled}
          onChange={(event) => onBodyChange(event.target.value)}
          placeholder="支持 [[wikilink]] 链接"
          className="mt-2 min-h-[160px]"
        />
      </details>
    </div>
  );
}

function seedFieldStatusLabel(status: SeedFieldStatus, anomaly?: string): string {
  if (status === "anomaly") return anomaly?.trim() || "该项数据被识别为异常";
  if (status === "filled") return "已补全";
  return "未补全";
}

function SeedFieldStatusIcon({
  status,
  anomaly,
}: {
  status: SeedFieldStatus;
  anomaly?: string;
}) {
  const label = seedFieldStatusLabel(status, anomaly);
  const className = cn(
    "size-3.5 shrink-0",
    status === "filled" && "text-emerald-600 dark:text-emerald-400",
    status === "anomaly" && "text-amber-600 dark:text-amber-400",
    status === "empty" && "text-muted-foreground/40",
  );

  return <CheckCircle2 className={className} aria-label={label} title={label} />;
}

function SeedField({
  fieldKey,
  schema,
  value,
  anomaly,
  disabled,
  onChange,
}: {
  fieldKey: string;
  schema: SeedPageSchema["properties"][string];
  value: unknown;
  anomaly?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}) {
  const label = schema.description ?? fieldKey;
  const placeholder = schema.ui?.placeholder ?? "";
  const widget = schema.ui?.widget ?? "input";
  const stringValue = typeof value === "string" ? value : value == null ? "" : String(value);
  const status = seedFieldStatus(value, anomaly);

  return (
    <div id={`seed-field-wrap-${fieldKey}`} className="space-y-2 scroll-mt-24">
      <Label htmlFor={`seed-field-${fieldKey}`} className="inline-flex items-center gap-1.5">
        <span>{label}</span>
        <SeedFieldStatusIcon status={status} anomaly={anomaly} />
      </Label>
      {widget === "textarea" ? (
        <AutosizeTextarea
          id={`seed-field-${fieldKey}`}
          value={stringValue}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <Input
          id={`seed-field-${fieldKey}`}
          value={stringValue}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </div>
  );
}

export function useDebouncedSave<T>(
  value: T,
  onSave: (value: T) => void | Promise<void>,
  delayMs = 800,
) {
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!dirty) return undefined;
    const timer = window.setTimeout(() => {
      void onSave(value);
      setDirty(false);
    }, delayMs);
    return () => window.clearTimeout(timer);
  }, [value, dirty, delayMs, onSave]);

  return {
    markDirty: () => setDirty(true),
  };
}
